import Clarity from '@microsoft/clarity';

let scriptInjected = false;
let tracking = false;
let trackedUserId: number | null = null;

/**
 * Starts Clarity for a session user, injecting the tag on first use and
 * granting consent explicitly so recording never depends on Clarity's default
 * consent state. Returns whether Clarity is tracking. No-ops outside
 * production so local/staging never record (matches PostHog).
 */
export function startClarityTracking(
  projectId: string | undefined,
  userId: number
): boolean {
  if (process.env.NEXT_PUBLIC_NODE_ENV !== 'production') return false;

  if (!projectId) return false;

  if (!scriptInjected) {
    Clarity.init(projectId);
    scriptInjected = true;
  }

  if (!tracking) {
    Clarity.consent(true);
    tracking = true;
  }

  trackedUserId = userId;

  return true;
}

/**
 * Revokes consent so Clarity stops recording once a session becomes
 * ineligible. The tag script cannot be unloaded, so consent revocation is the
 * supported stop signal.
 */
export function stopClarityTracking(): void {
  trackedUserId = null;

  if (!scriptInjected || !tracking) return;

  Clarity.consent(false);
  tracking = false;
}

/**
 * Revokes recording granted to a different user. Must run synchronously on a
 * user switch so the incoming user's session is never recorded under the
 * previous user's consent.
 */
export function stopClarityTrackingForOtherUser(userId: number): void {
  if (trackedUserId === null || trackedUserId === userId) return;

  stopClarityTracking();
}

/** Tags the active Clarity session; no-op while tracking is stopped. */
export function setClarityTag(key: string, value: string): void {
  if (!tracking) return;

  Clarity.setTag(key, value);
}

export function isClarityTracking(): boolean {
  return tracking;
}
